import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionStorage, STORAGE_KEYS } from '../utils/sessionStorage.js';
import { DEFAULT_PERMISSIONS } from '../utils/adminPermission.js';
import { clearAuthStatus, getStoredToken, isLaterTokenOfSameLogin } from '../api/auth.js';

function readIdentity() {
  return sessionStorage.get(STORAGE_KEYS.AUTH_IDENTITY);
}

export default function AuthGate({ children }) {
  const navigate = useNavigate();

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  const token = params.get('token');
  const type = params.get('type');
  const isAdminLink = !!token && type === 'moderator';

  const [state, setState] = useState(() => {
    if (token) return isAdminLink ? 'verifying' : 'unauthorized';
    const identity = readIdentity();
    if (identity && String(identity.sessionId) === String(sessionId)) return 'idle';
    return 'unauthorized';
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdminLink) return;

    if (!sessionId) {
      setError('This link is missing a session id (?session_id=…).');
      setState('error');
      return;
    }

    // The same link opened again in this tab: its token was retired when this tab refreshed
    // it, and the tab already holds the later one of the same login - keep that.
    if (!isLaterTokenOfSameLogin(getStoredToken(), token)) {
      sessionStorage.set(STORAGE_KEYS.AUTH_TOKEN, token);
    }
    sessionStorage.set(STORAGE_KEYS.AUTH_IDENTITY, {
      userId: `admin-${sessionId}`,
      userType: "moderator",
      permission: DEFAULT_PERMISSIONS,
      sessionId,
    });
    // A fresh link starts clean: drop an earlier "link expired" from this tab. Whether the
    // link itself has expired is for AdminAuthGate to settle - with the server, not the
    // device clock, which may be off.
    clearAuthStatus();
    navigate(`/admin/sessiondetails/${sessionId}/dashboard`, { replace: true });
  }, [isAdminLink, token, sessionId, navigate]);

  if (state === 'verifying') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
        Verifying your access link…
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="font-semibold text-gray-800">Couldn't verify this link</p>
        <p className="max-w-sm text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  if (state === 'unauthorized') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="font-semibold text-gray-800">Access denied</p>
        <p className="max-w-sm text-sm text-gray-500">This page requires a valid access link. Ask the session admin for yours.</p>
      </div>
    );
  }

  return children;
}
